import { useState } from 'react';
import { api } from '../api.js';
import { useConfirm } from './ConfirmDialog.jsx';
import { END_CHOICES, residentName } from '../livestock-journey.js';
import { NotePhotoPicker } from './NotePhotoPicker.jsx';
import { uploadNotePhotos, photosFailedMessage } from '../note-photos.js';
import { localDateIso } from '../local-date.js';

// The keeper's own calendar day: toISOString is UTC, which is tomorrow
// every evening west of Greenwich and yesterday every morning east of it.
const localToday = () => localDateIso();

// "What happened?" when a resident leaves the tank. It stays in the
// history with its photos: only "Added by mistake" deletes it. When a row
// holds several of the same species, the keeper says how many, and those
// split off into their own record while the rest stay in the tank.
// The day it went in, as the server checks it. A resident dated by the old
// UTC clock can carry tomorrow's date on a US evening; the sheet then
// starts on that day instead of a today the server refuses.
export function endDateBounds(addedAt, today = localToday()) {
  const added = addedAt ? String(addedAt).slice(0, 10) : '';
  const min = /^\d{4}-\d{2}-\d{2}$/.test(added) ? added : '';
  return { min, start: min && min > today ? min : today };
}

function dayWords(iso) {
  const d = new Date(`${iso}T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

export function ResidentEndSheet({ tank, resident, onClose, onDone }) {
  const confirm = useConfirm();
  const qty = Number(resident.qty) || 1;
  const bounds = endDateBounds(resident.added_at);
  const [status, setStatus] = useState(null);
  const [endedAt, setEndedAt] = useState(bounds.start);
  const [count, setCount] = useState(qty);
  const [note, setNote] = useState('');
  const [photos, setPhotos] = useState([]);
  const [finished, setFinished] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const save = async () => {
    if (!status) {
      setErr('Pick what happened.');
      return;
    }
    if (bounds.min && endedAt && endedAt < bounds.min) {
      setErr(`It was added on ${dayWords(bounds.min)}. Pick that day or later.`);
      return;
    }
    setBusy(true);
    setErr('');
    try {
      const out = await api(`/api/tanks/${tank.id}/livestock/${resident.id}/end`, {
        method: 'POST',
        body: {
          status,
          ended_at: endedAt || bounds.start,
          note: note.trim() || null,
          count,
        },
      });
      // Photos go on the ended record (a split-off one when only some of
      // the row left), so they sit with what happened in its history.
      const endedId = out?.ended?.id || resident.id;
      const photoErrors = [];
      const failed = photos.length
        ? await uploadNotePhotos(
            `/api/tanks/${tank.id}/livestock/${endedId}/image`,
            photos,
            photoErrors
          )
        : 0;
      if (failed) {
        // The record is saved; say so, and leave only Close.
        setErr(
          `${photosFailedMessage(failed, photos.length, photoErrors[0])} Add them again from its record in History.`
        );
        setPhotos([]);
        setFinished(true);
        setBusy(false);
        return;
      }
      await onDone?.();
      onClose();
    } catch (e) {
      setErr(e.message || 'Could not save.');
      setBusy(false);
    }
  };

  // Once the record is saved (only its photos failed), every way out of
  // the sheet must refresh the caller, or the ended resident stays on Home.
  const close = async () => {
    if (finished) await onDone?.();
    onClose();
  };

  const deleteByMistake = async () => {
    const ok = await confirm({
      title: `Delete ${residentName(resident)}?`,
      body: 'Only for a resident added by mistake. It leaves no history, and its photos are deleted too.',
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    setBusy(true);
    setErr('');
    try {
      await api(`/api/tanks/${tank.id}/livestock/${resident.id}`, { method: 'DELETE' });
      await onDone?.();
      onClose();
    } catch (e) {
      setErr(e.message || 'Could not delete.');
      setBusy(false);
    }
  };

  return (
    <div
      className="hv-resident-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={`What happened to ${residentName(resident)}?`}
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) close();
      }}
    >
      <div className="hv-resident-sheet">
        <header className="hv-resident-sheet-head">
          <div>
            <div className="hv-resident-sheet-eyebrow font-heading">No longer in the tank</div>
            <h3 className="hv-resident-sheet-title">{residentName(resident)}</h3>
          </div>
          <button
            type="button"
            className="hv-resident-sheet-close"
            onClick={close}
            aria-label="Close"
            disabled={busy}
          >
            ×
          </button>
        </header>

        <p className="rj-lede">
          It stays in your history with its photos, so you keep a record of it.
        </p>

        <div className="rj-label font-heading">What happened?</div>
        <div className="rj-choices" role="radiogroup" aria-label="What happened">
          {END_CHOICES.map((c) => (
            <button
              key={c.k}
              type="button"
              role="radio"
              aria-checked={status === c.k}
              className={`rj-choice${status === c.k ? ' is-on' : ''}`}
              onClick={() => setStatus(c.k)}
              disabled={busy}
            >
              <span className="rj-choice-label">{c.label}</span>
              {c.hint && <span className="rj-choice-hint">{c.hint}</span>}
            </button>
          ))}
        </div>

        {qty > 1 && (
          <div className="hv-resident-row">
            <div className="hv-resident-field-label font-heading">How many of the {qty}?</div>
            <div className="hv-resident-qty-stepper">
              <button
                type="button"
                onClick={() => setCount((n) => Math.max(1, n - 1))}
                disabled={busy || count <= 1}
                aria-label="Fewer"
              >
                −
              </button>
              <span className="font-mono">{count}</span>
              <button
                type="button"
                onClick={() => setCount((n) => Math.min(qty, n + 1))}
                disabled={busy || count >= qty}
                aria-label="More"
              >
                +
              </button>
            </div>
          </div>
        )}

        <label className="rj-field">
          <span className="rj-label font-heading">When</span>
          <input
            type="date"
            value={endedAt}
            min={bounds.min || undefined}
            max={bounds.start}
            onChange={(e) => setEndedAt(e.target.value)}
            disabled={busy}
          />
        </label>

        <label className="rj-field">
          <span className="rj-label font-heading">Notes (optional)</span>
          <textarea
            rows={3}
            maxLength={1000}
            placeholder="What happened, in your own words. Future you will thank you."
            value={note}
            onChange={(e) => setNote(e.target.value)}
            disabled={busy}
          />
        </label>

        <div className="rj-label font-heading">Photos (optional)</div>
        <NotePhotoPicker files={photos} onChange={setPhotos} disabled={busy} />

        {err && (
          <p className="hv-resident-err" role="alert">
            {err}
          </p>
        )}

        {finished ? (
          <div className="hv-resident-actions">
            <button type="button" className="hv-resident-action save font-heading" onClick={close}>
              Close
            </button>
          </div>
        ) : (
          <div className="hv-resident-actions">
            <button
              type="button"
              className="hv-resident-action font-heading"
              onClick={onClose}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              type="button"
              className="hv-resident-action save font-heading"
              onClick={save}
              disabled={busy}
            >
              {busy ? '…' : 'Save'}
            </button>
          </div>
        )}
        {!finished && (
          <button type="button" className="rj-mistake" onClick={deleteByMistake} disabled={busy}>
            Added by mistake? Delete it instead
          </button>
        )}
      </div>
    </div>
  );
}
